import { ErrorMessage } from '@hookform/error-message';
import { useFormContext } from 'react-hook-form';
import type {
  FieldErrors,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { useRef, useEffect, useState, useContext } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from '@/utils/i18n/useTranslation';

import { ModalContext } from '@/context/ModalContext';

import FormDialog from '@/components/FormDialog/FormDialog';
import FormError from '@/components/FormError/FormError';
import { UserContext } from '@/context/UserContext';
import type {
  FormItemComponentProps,
  FormItemRequiredState,
  FormFieldOption,
} from '@/types/domain';

// `errors` is read off the context but RHF exposes it on `formState.errors`; see FormItemTextfield.
type FormContextWithErrors = UseFormReturn & { errors?: FieldErrors };

const emptyClass = ' not-empty';
const revealClass = ' is-revealed';
const activeClass = ' is-active';

const requiredHint = ' *';

const FormItemSelect = (props: FormItemComponentProps) => {
  const {
    item,
    onFormItemChange,
    onFormItemLoad,
    groupedState,
    revealState,
    filteredState,
    enabledState,
    shouldValidateState,
  } = props;

  const userContext = useContext(UserContext);
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED');

  const { register, watch, errors, formState, trigger, setValue } =
    useFormContext() as unknown as FormContextWithErrors;
  const modalContext = useContext(ModalContext);

  const [isRequired, setIsRequired] = useState<FormItemRequiredState>({
    required: false,
  });
  const [isGrouped, setIsGrouped] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
  const [isDisabled, setIsDisabled] = useState(!item?.disabled);
  const [filteredOptions, setFilteredOptions] = useState<
    FormFieldOption[] | false
  >(false);
  const [dialogEvent, setDialogEvent] = useState<
    ChangeEvent<HTMLSelectElement> | false
  >(false);
  const [previousValue, setPreviousValue] = useState<unknown>(false);
  const [selectHint, setSelectHint] = useState<FormFieldOption[] | false>(
    false,
  );

  const itemProps = item;
  const itemRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemValue = watch(item.name!) as string;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemInputRef = register(item.name!, isRequired as RegisterOptions);

  useEffect(() => {
    if (!item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: requiredErrorMessage });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, []);

  useEffect(() => {
    if (!item.revealed || !item.required) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    setIsRequired({ required: revealState ? requiredErrorMessage : false });
    void trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [revealState]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    if (!shouldValidateState) setIsRequired({ required: false });
  }, [shouldValidateState]);

  useEffect(() => {
    if (!itemValue) return;
    void trigger();
    if (!isLoaded && onFormItemLoad)
      onFormItemLoad({ itemProps, itemRef, itemValue });
    else if (onFormItemChange)
      onFormItemChange({ itemProps, itemRef, itemValue });
    /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (item.hint)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setSelectHint(
        item.options!.filter(
          (option) => option.value === itemValue && option.hint,
        ),
      );
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [itemValue]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!item?.group || !groupedState) return;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    const itemLabel = item?.group?.label;
    const groupedItem = groupedState.find((str) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return str.label.includes(itemLabel!);
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, react-hooks/set-state-in-effect -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setIsGrouped(groupedItem!.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [groupedState]);

  useEffect(() => {
    if (!filteredState) return;
    if (filteredOptions !== filteredState) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setFilteredOptions(filteredState);
      void trigger();
    }
    setIsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [filteredState]);

  useEffect(() => {
    if (filteredOptions && itemValue)
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
        setValue(item.name!, itemValue);
      }, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [filteredOptions]);

  useEffect(() => {
    const enables = enabledState?.filter((input) => input.name === item.name);
    const isOptionDisabled =
      enables?.[0]?.name === item.name ? !enables?.[0]?.enabled : false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    setIsDisabled(isOptionDisabled);
    /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (
      enables?.[0]?.toEmpty &&
      isLoaded &&
      itemValue !== '' &&
      itemValue !== null &&
      itemValue !== undefined
    ) {
      setTimeout(() => {
        setValue(item.name!, null);
      }, 10);
    }
    /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [enabledState]);

  useEffect(() => {
    if (isDisabled && ['', null, undefined].includes(itemValue))
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
        setValue(item.name!, null);
      }, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [isDisabled]);

  useEffect(() => {
    if (!item.dialog) return;
    modalContext.setModalDialog(dialogEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [dialogEvent]);

  const onSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (item.dialog) {
      setPreviousValue(itemValue);
      setDialogEvent(e);
    } else {
      void itemInputRef.onChange(e);
    }
  };

  const onFormDialogConfirm = () => {
    void itemInputRef.onChange(dialogEvent as ChangeEvent<HTMLSelectElement>);
    setDialogEvent(false);
  };

  const onFormDialogDecline = () => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setValue(item.name!, previousValue);
    setDialogEvent(false);
  };

  /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${String(item.type)}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        itemValue ? emptyClass : ''
      }${revealState ? revealClass : ''}${isDisabled ? ' is-disabled' : ''}${dialogEvent ? ' has-dialog' : ''}${
        isGrouped ? activeClass : ''
      }`}
      data-reveal-target={item.revealed}
    >
      <label className="FormItem-label" htmlFor={item.name}>
        {item.transl ? t(item.transl as string) : item.label}
        {item.required ? requiredHint : ''}
      </label>
      <select
        id={item.name}
        name={item.name}
        className={`FormItem-input FormItem-input--${String(item.type)}`}
        defaultValue={item.value || ''}
        onChange={(e) => {
          onSelectChange(e);
        }}
        ref={itemInputRef.ref}
      >
        {filteredOptions &&
          item.options!.length > 0 &&
          filteredOptions.map((option, index) => {
            return (
              <option
                value={option.value}
                disabled={option.disabled}
                key={`FormItem-option--${String(index + 1)}`}
              >
                {option.transl ? t(option.transl) : option.label}
                {option.suffix ? ` ${t(option.suffix)}` : ''}
              </option>
            );
          })}
        {!filteredOptions &&
          item.options!.length > 0 &&
          item.options!.map((option, index) => {
            return (
              <option
                value={option.value}
                disabled={option.disabled}
                key={`FormItem-option--${String(index + 1)}`}
              >
                {option.transl ? t(option.transl) : option.label}
                {option.suffix ? ` ${t(option.suffix)}` : ''}
              </option>
            );
          })}
      </select>
      {(selectHint as FormFieldOption[])?.[0] && itemValue && (
        <div className="FormItem-hint">
          <p>{(selectHint as FormFieldOption[])?.[0]?.hint}</p>
        </div>
      )}
      {dialogEvent && (
        <FormDialog
          dialogEvent={dialogEvent}
          item={item}
          enabledState={enabledState}
          onFormDialogConfirm={onFormDialogConfirm}
          onFormDialogDecline={onFormDialogDecline}
        />
      )}
      {formState.isSubmitted && !isDisabled && (
        <ErrorMessage errors={errors} name={item.name!} as={<FormError />} />
      )}
    </div>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
};

export default FormItemSelect;
